import { useState, useRef, useCallback, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { MessageSquare, X, MoreVertical, Users, Building2, Maximize2, Minimize2, Volume2, VolumeX } from 'lucide-react';
import ChatList from './ChatList';
import ChatView from './ChatView';
import CreateGroupDialog from './CreateGroupDialog';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUnreadCounts, isNotificationMuted, setNotificationMuted } from '@/hooks/useUnreadCounts';

interface Room {
  id: string;
  type: string;
  name: string | null;
  otherUserName?: string;
}

const MessagingWidget = () => {
  const { t, dir } = useLanguage();
  const { user } = useAuth();
  const { unreadByRoom, totalUnread, markAsRead } = useUnreadCounts();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showCreateDept, setShowCreateDept] = useState(false);
  const [deptName, setDeptName] = useState('');
  const [creatingDept, setCreatingDept] = useState(false);
  const [chatListKey, setChatListKey] = useState(0);
  const [soundMuted, setSoundMuted] = useState(() => isNotificationMuted());

  // Chat window draggable & resizable state
  const [chatPos, setChatPos] = useState(() => {
    const saved = localStorage.getItem('msg-chat-pos');
    return saved ? JSON.parse(saved) : null;
  });
  const [chatSize, setChatSize] = useState(() => {
    const saved = localStorage.getItem('msg-chat-size');
    return saved ? JSON.parse(saved) : { w: 380, h: 500 };
  });
  const [isMaximized, setIsMaximized] = useState(false);
  const chatDragging = useRef(false);
  const chatDragOffset = useRef({ x: 0, y: 0 });
  const chatRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const resizing = useRef(false);
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

  // Close menu on click outside
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  // Chat window drag handlers
  const onChatHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    if (isMaximized) return;
    e.preventDefault();
    const rect = chatRef.current?.getBoundingClientRect();
    if (!rect) return;
    chatDragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    chatDragging.current = false;
    const onMove = (ev: MouseEvent) => {
      chatDragging.current = true;
      const x = Math.max(0, Math.min(window.innerWidth - chatSize.w, ev.clientX - chatDragOffset.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - 60, ev.clientY - chatDragOffset.current.y));
      setChatPos({ x, y });
    };
    const onUp = () => {
      if (chatDragging.current) {
        setChatPos((pos: any) => {
          if (pos) localStorage.setItem('msg-chat-pos', JSON.stringify(pos));
          return pos;
        });
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [isMaximized, chatSize.w]);

  // Chat window touch drag
  const onChatHeaderTouchStart = useCallback((e: React.TouchEvent) => {
    if (isMaximized) return;
    const touch = e.touches[0];
    const rect = chatRef.current?.getBoundingClientRect();
    if (!rect) return;
    chatDragOffset.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    chatDragging.current = false;
    const onMove = (ev: TouchEvent) => {
      ev.preventDefault();
      chatDragging.current = true;
      const t = ev.touches[0];
      const x = Math.max(0, Math.min(window.innerWidth - chatSize.w, t.clientX - chatDragOffset.current.x));
      const y = Math.max(0, Math.min(window.innerHeight - 60, t.clientY - chatDragOffset.current.y));
      setChatPos({ x, y });
    };
    const onUp = () => {
      if (chatDragging.current) {
        setChatPos((pos: any) => {
          if (pos) localStorage.setItem('msg-chat-pos', JSON.stringify(pos));
          return pos;
        });
      }
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [isMaximized, chatSize.w]);

  // Resize handler (bottom-left corner)
  const onResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    resizeStart.current = { x: e.clientX, y: e.clientY, w: chatSize.w, h: chatSize.h };
    const onMove = (ev: MouseEvent) => {
      const dw = resizeStart.current.x - ev.clientX; // left resize = increase width
      const dh = ev.clientY - resizeStart.current.y;
      const newW = Math.max(300, Math.min(700, resizeStart.current.w + dw));
      const newH = Math.max(350, Math.min(window.innerHeight - 40, resizeStart.current.h + dh));
      setChatSize({ w: newW, h: newH });
    };
    const onUp = () => {
      resizing.current = false;
      setChatSize((s: any) => {
        localStorage.setItem('msg-chat-size', JSON.stringify(s));
        return s;
      });
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [chatSize]);

  const toggleMaximize = useCallback(() => {
    setIsMaximized(prev => !prev);
  }, []);

  // Draggable button state
  const [btnPos, setBtnPos] = useState(() => {
    const saved = localStorage.getItem('msg-btn-pos');
    return saved ? JSON.parse(saved) : { x: 24, y: null as number | null };
  });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (btnPos.y === null) {
      // Default position: bottom-24 equivalent
      setBtnPos({ x: 24, y: window.innerHeight - 96 - 56 });
    }
  }, []);

  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    dragOffset.current = { x: clientX - rect.left, y: clientY - rect.top };
    dragging.current = false;
  }, []);

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    dragging.current = true;
    const x = Math.max(0, Math.min(window.innerWidth - 56, clientX - dragOffset.current.x));
    const y = Math.max(0, Math.min(window.innerHeight - 56, clientY - dragOffset.current.y));
    setBtnPos({ x, y });
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragging.current) {
      setBtnPos((pos: { x: number; y: number }) => {
        localStorage.setItem('msg-btn-pos', JSON.stringify(pos));
        return pos;
      });
    }
  }, []);

  // Mouse events
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
    const onMove = (ev: MouseEvent) => handleDragMove(ev.clientX, ev.clientY);
    const onUp = () => {
      handleDragEnd();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [handleDragStart, handleDragMove, handleDragEnd]);

  // Touch events
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
    const onMove = (ev: TouchEvent) => {
      ev.preventDefault();
      handleDragMove(ev.touches[0].clientX, ev.touches[0].clientY);
    };
    const onUp = () => {
      handleDragEnd();
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  }, [handleDragStart, handleDragMove, handleDragEnd]);

  const handleBtnClick = () => {
    if (!dragging.current) {
      setIsOpen(!isOpen);
    }
    dragging.current = false;
  };

  const handleCreateDept = async () => {
    if (!user || !deptName.trim() || creatingDept) return;
    setCreatingDept(true);

    const { data: newRoom, error } = await supabase
      .from('chat_rooms')
      .insert({ type: 'department' as any, name: deptName.trim(), created_by: user.id })
      .select()
      .single();

    if (!error && newRoom) {
      await supabase.from('chat_room_members').insert({ room_id: newRoom.id, user_id: user.id } as any);
      setChatListKey(k => k + 1);
    }

    setCreatingDept(false);
    setDeptName('');
    setShowCreateDept(false);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleBtnClick}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        style={{
          position: 'fixed',
          left: btnPos.x,
          top: btnPos.y ?? undefined,
          bottom: btnPos.y === null ? 96 : undefined,
          zIndex: 50,
          touchAction: 'none',
          cursor: 'grab',
        }}
        className="w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg flex items-center justify-center hover:bg-[#128C7E] transition-colors hover:scale-110 relative"
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
        {!isOpen && totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          ref={chatRef}
          dir="rtl"
          className={`fixed z-50 overflow-hidden shadow-2xl flex flex-col ${isMaximized ? 'rounded-none' : 'sm:rounded-xl'}`}
          style={
            isMaximized
              ? { left: 0, top: 0, width: '100vw', height: '100vh' }
              : window.innerWidth < 640
                ? { left: 0, bottom: 0, width: '100%', height: '100vh' }
                : {
                    left: chatPos?.x ?? 24,
                    top: chatPos?.y ?? undefined,
                    bottom: chatPos ? undefined : 140,
                    width: chatSize.w,
                    height: chatSize.h,
                  }
          }
        >
          {selectedRoom ? (
            <ChatView room={selectedRoom} onBack={() => setSelectedRoom(null)} onMarkRead={() => markAsRead(selectedRoom.id)} />
          ) : (
            <>
              <div
                className="bg-[#075E54] text-white px-4 py-3 shrink-0 flex items-center justify-between select-none"
                style={{ cursor: isMaximized ? 'default' : 'grab' }}
                onMouseDown={onChatHeaderMouseDown}
                onTouchStart={onChatHeaderTouchStart}
              >
                <h2 className="text-lg font-bold">{t('groups')}</h2>
                <div className="flex items-center gap-1">
                  <button onClick={toggleMaximize} className="p-1 rounded-full hover:bg-white/10 transition-colors">
                    {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </button>
                  <button onClick={() => setIsOpen(false)} className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                    <X className="h-4 w-4" />
                  </button>
                  <div className="relative" ref={menuRef}>
                    <button onClick={() => setShowMenu(!showMenu)} className="p-1 rounded-full hover:bg-white/10 transition-colors">
                      <MoreVertical className="h-5 w-5" />
                    </button>
                    {showMenu && (
                      <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl py-1 z-50 min-w-[180px]">
                        <button
                          onClick={() => { setShowCreateDept(true); setShowMenu(false); }}
                          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-[#111b21] hover:bg-[#f0f0f0] transition-colors"
                        >
                          <Building2 className="h-4 w-4 text-[#54656f]" />
                          <span>{t('newGroup')}</span>
                        </button>
                        <button
                          onClick={() => { const next = !soundMuted; setSoundMuted(next); setNotificationMuted(next); setShowMenu(false); }}
                          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-[#111b21] hover:bg-[#f0f0f0] transition-colors"
                        >
                          {soundMuted ? <VolumeX className="h-4 w-4 text-[#54656f]" /> : <Volume2 className="h-4 w-4 text-[#54656f]" />}
                          <span>{soundMuted ? t('unmute') || 'تفعيل الصوت' : t('mute') || 'كتم الصوت'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-1 bg-white overflow-hidden flex flex-col">
                <ChatList key={chatListKey} activeTab="departments" onSelectRoom={(room) => { markAsRead(room.id); setSelectedRoom(room); }} unreadByRoom={unreadByRoom} />
              </div>
            </>
          )}
          {/* Resize handle - bottom left corner (desktop only, not maximized) */}
          {!isMaximized && window.innerWidth >= 640 && (
            <div
              onMouseDown={onResizeMouseDown}
              className="absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize z-50"
              style={{ touchAction: 'none' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" className="text-gray-400">
                <path d="M14 16L16 14M10 16L16 10M6 16L16 6" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </div>
          )}
        </div>
      )}

      <CreateGroupDialog open={showCreateGroup} onClose={() => setShowCreateGroup(false)} onCreated={() => setChatListKey(k => k + 1)} />

      <Dialog open={showCreateDept} onOpenChange={(v) => { if (!v) { setShowCreateDept(false); setDeptName(''); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader className="bg-[#075E54] text-white px-4 py-3 -mx-6 -mt-6 rounded-t-lg">
            <DialogTitle className="text-white">{t('newDepartment')}</DialogTitle>
          </DialogHeader>
          <div className="pt-4 space-y-4">
            <Input
              value={deptName}
              onChange={(e) => setDeptName(e.target.value)}
              placeholder={t('deptNamePlaceholder')}
              className="border-0 border-b-2 border-[#25D366] rounded-none focus-visible:ring-0 text-[#111b21]"
            />
            <Button
              onClick={handleCreateDept}
              disabled={!deptName.trim() || creatingDept}
              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white"
            >
              {creatingDept ? t('creatingDept') : t('createDept')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessagingWidget;
